import React, { useEffect, useMemo } from 'react';
import { Formik, Form, Field, FieldProps } from 'formik';
import * as Yup from 'yup';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import BaseForm from '../../../../../../../components/shared/form-controls/base-form';
import { useAppDispatch, useAppSelector } from '../../../../../../../hooks/hooks';
import { WorkCalendar } from '../../../../../../../features/settings/workcalendar/types';
import { workHoursStringArray } from '../../../types';
import { getHours, getRegion } from '../functions';
import { EditForm } from '../types';
import { FormInput } from '../../../../../../../components/shared/form-controls/form-input';
import { updateWorkCalendar, getRegions } from '../../../../../../../features/settings/workcalendar';
import { NAVIGATION_ROUTES } from '../../../../../../../routes/NavigationRoute';
import { DropdownField } from '../../../../../../../components/shared/nextgen/dropdown-field';
import { Lx } from '../../../../../../../i18n/consts';
import { FormCheckbox } from '../../../../../../../components/shared/form-controls/form-checkbox';
import { canManageWorkCalendars } from '../../../../../../../features/profile/entityPermissions';

interface LocationState {
    calendar?: WorkCalendar;
}

const EditWorkCalendarForm = (): JSX.Element | null => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const profile = useAppSelector(store => store.profile);
    const { calendar } = (location.state as LocationState) ?? {};
    const regions = useAppSelector(store => store.workCalendar.regions);
    const canEditWorkCalendars = canManageWorkCalendars(profile);

    useEffect(() => {
        if (!canEditWorkCalendars) {
            navigate(NAVIGATION_ROUTES.SETTINGS_CALENDAR, { replace: true });
            return;
        }

        if (!regions.length) {
            dispatch(getRegions());
        }
    }, [canEditWorkCalendars, dispatch, navigate, regions.length]);

    useEffect(() => {
        if (!canEditWorkCalendars || !calendar) {
            navigate(NAVIGATION_ROUTES.SETTINGS_CALENDAR, { replace: true });
        }
    }, [calendar, canEditWorkCalendars, navigate]);

    const initialValues: EditForm | null = useMemo(() => {
        if (!calendar || !regions.length) {
            return null;
        }

        const selectedRegion = getRegion(calendar.region, regions) ?? regions[0];

        return {
            name: calendar.name,
            region: selectedRegion,
            workHours: getHours(calendar.workHours),
            shortworkHours: getHours(calendar.shortWorkHours),
            isRoot: calendar.isRoot
        };
    }, [regions, calendar]);

    if (!canEditWorkCalendars || !calendar || !initialValues) {
        return null;
    }

    const validationSchema: Yup.Schema<EditForm> = Yup.object().shape({
        name: Yup.string()
            .max(45, 'Must be 45 characters or less')
            .required(intl.formatMessage({ id: Lx.Form.NAME_REQUIRED_FORMAT }, { s: intl.formatMessage({ id: Lx.General.WORK_CALENDAR }) })),
        region: Yup.object().shape({
            id: Yup.number().required(),
            name: Yup.string().required()
        }).required(),
        workHours: Yup.object().shape({
            id: Yup.number().required(),
            name: Yup.string().required()
        }).required(),
        shortworkHours: Yup.object().shape({
            id: Yup.number().required(),
            name: Yup.string().required()
        }).required(),
        isRoot: Yup.boolean().required()
    });

    const save = async (values: EditForm) => {
        const updatedCalendar: WorkCalendar = {
            id: calendar.id,
            name: values.name,
            region: values.region.id,
            workHours: values.workHours.id,
            shortWorkHours: values.shortworkHours.id,
            isRoot: values.isRoot
        };

        await dispatch(updateWorkCalendar({ workCalendar: updatedCalendar })).unwrap();
    };

    const handleClose = () => {
        navigate(NAVIGATION_ROUTES.SETTINGS_CALENDAR, {
            state: {
                selectedCalendarId: calendar.id,
            },
        });
    };

    return (
        <Formik initialValues={initialValues} enableReinitialize validationSchema={validationSchema} onSubmit={save}>
            <Form>
                <BaseForm
                    contentGap={24}
                    formType='edit'
                    formHeader={Lx.Calendar.EDIT_WORK_CALENDAR}
                    elementName={calendar.name}
                    savingTitle={intl.formatMessage({ id: Lx.General.WORK_CALENDAR })}
                    onClose={handleClose}
                    onAfterSave={handleClose}>
                    <div style={{ width: '100%', maxWidth: '500px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        <FormInput label={intl.formatMessage({ id: Lx.Calendar.NAME })} name='name' />
                        <DropdownField
                            required
                            label={intl.formatMessage({ id: Lx.Calendar.REGION })}
                            name='region'
                            items={regions}
                            description={intl.formatMessage({ id: Lx.Calendar.EDIT_CALENDAR_DESC })}
                        />
                        <DropdownField
                            required
                            label={intl.formatMessage({ id: Lx.Calendar.WORKHOURS_IN_DAY })}
                            name='workHours'
                            items={workHoursStringArray}
                            description={intl.formatMessage({ id: Lx.Calendar.EDIT_CALENDAR_DESC })}
                        />
                        <DropdownField
                            required
                            label={intl.formatMessage({ id: Lx.Calendar.WORKHOURS_IN_SHORT_DAY })}
                            name='shortworkHours'
                            items={workHoursStringArray}
                            description={intl.formatMessage({ id: Lx.Calendar.EDIT_CALENDAR_DESC })}
                        />
                        <FormCheckbox name='isRoot' label={intl.formatMessage({ id: Lx.Calendar.PRIMARY })} />
                    </div>
                </BaseForm>
            </Form>
        </Formik>
    );
};

export default React.memo(EditWorkCalendarForm);
